import { Button, CloseButton, Loader, Popover, Switch, Text, TextInput, UnstyledButton } from "@mantine/core";
import type { UseQueryResult } from "@tanstack/react-query";
import { Check, Search } from "lucide-react";
import { useId, useState, type ReactElement, type ReactNode } from "react";

import { useListProducts } from "../../api/generated/endpoints/teams/teams";
import type { Product, ProductImage, ProductImageTag } from "../../api/generated/model";
import { ErrorAlert } from "../ErrorAlert";
import { Soon } from "../../ui/Soon";
import { ROOM_ABOVE } from "../../ui/popover-room";
import classes from "./ProductPicker.module.css";

/** The product photo a change starts from, with what the composer shows of it. */
export interface ProductChoice {
  productId: string;
  assetId: string;
  url: string | null;
  /** The product's name. */
  name: string;
}

/** Live's working Brand Logo switch (`ProductAssetsSelect`'s `brandLogo`), as a consumer that takes
 * the brand's logo hands it: whether the brand has one (none: off and disabled), its picture, and
 * whether it is on as the picker opens. */
export interface BrandLogoSwitch {
  available: boolean;
  url: string | null;
  on: boolean;
}

/** What Confirm hands back beside the photo: the brand assets as the member left them. */
export interface PickedAssets {
  brandLogo: boolean;
}

/** Live's captions under a product's images (its tag). */
const TAG_LABEL: Record<ProductImageTag, string> = {
  product_shot: "Product Shot",
  in_use: "In Use",
  packaging: "Packaging",
  other: "Other",
};

/** A product's photos that have a picture to show: a photo without a signed URL is left out. */
function photosOf(product: Product): ProductImage[] {
  return product.images.filter((image) => image.url !== null);
}

/** The photo chosen first, as live preselects: the main photo, else a product shot, else any. */
function firstPhoto(product: Product): ProductImage | undefined {
  const photos = photosOf(product);
  return (
    photos.find((image) => image.is_main === true) ??
    photos.find((image) => image.tag === "product_shot") ??
    photos[0]
  );
}

/** Live's search is loose: the name, the tagline and the description, in any case. */
function matches(product: Product, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return [product.name, product.tagline, product.description]
    .some((text) => text?.toLowerCase().includes(needle) === true);
}

interface ProductPickerProps {
  teamId: string;
  brandId: string;
  /** The product photo the draft starts from, or null: reopening shows it. */
  chosen: ProductChoice | null;
  /** Live's line under the title for the composer that opened it. */
  subtitle?: string;
  /** Live's `hasTypography` and `hasColors`: false leaves the switch out (a template's picker). */
  typography?: boolean;
  colors?: boolean;
  /** Live's working Brand Logo switch, for a consumer that takes the brand's logo; left out, Brand
   * Logo is Coming soon, as the Studio's. */
  brandLogo?: BrandLogoSwitch;
  /** The photo a product starts with, of those with a picture: the main one by default (the
   * Studio's); a template passes live's `getMainProductImage`. */
  preselect?: (product: Product) => ProductImage | null | undefined;
  /** False: step 2 is the product's photos alone, live's image slot's "From product"
   * (`ProductImageSelectPopover`, issue 739), with no brand assets or settings. Left out: live's
   * "Select Images & Assets" (the Studio's). */
  assets?: boolean;
  opened: boolean;
  onClose: () => void;
  onConfirm: (choice: ProductChoice, assets: PickedAssets) => void;
  /**
   * What the popover hangs from: `wrap` makes an element its anchor (the Products tool's button,
   * or the video's start-frame slot).
   */
  anchor: (wrap: (element: ReactElement) => ReactElement) => ReactNode;
}

/**
 * Live's "Products" popover (G1 decision 22). Step 1 lists the brand's products with a search; a
 * click opens step 2 beside it, the product's images with live's brand assets and colors (Coming
 * soon, or as the consumer sets them), or its photos alone (`assets` false, a template's image
 * slot), and "Confirm" makes the chosen photo the draft's source. The API takes one source, so one
 * product and one of its photos.
 */
export function ProductPicker(props: ProductPickerProps) {
  const { teamId, brandId, chosen, opened, onClose, onConfirm, anchor } = props;
  const options: PaneOptions = {
    typography: props.typography !== false,
    colors: props.colors !== false,
    brandLogo: props.brandLogo ?? null,
    preselect: props.preselect ?? firstPhoto,
    assets: props.assets !== false,
  };
  // Live allows up to 4 in the simple composer; the API takes one source.
  const subtitle = props.subtitle ?? "You can select 1 product";
  const titleId = useId();
  return (
    <Popover
      opened={opened}
      onChange={(next) => (next ? undefined : onClose())}
      position="top-start"
      offset={8}
      // Never flipped under its anchor: it takes the room above, below the header (the room
      // above the video's start frame is smaller than above the image tools).
      middlewares={ROOM_ABOVE}
      trapFocus
      returnFocus
    >
      {anchor((element) => (
        <Popover.Target>{element}</Popover.Target>
      ))}
      <Popover.Dropdown className={classes.dropdown} aria-labelledby={titleId}>
        {/* Mounted while open: each opening starts from what the draft holds. */}
        <Panes
          teamId={teamId}
          brandId={brandId}
          chosen={chosen}
          subtitle={subtitle}
          titleId={titleId}
          options={options}
          onClose={onClose}
          onConfirm={(choice, assets) => {
            onConfirm(choice, assets);
            onClose();
          }}
        />
      </Popover.Dropdown>
    </Popover>
  );
}

/** The step 2 settings a consumer chose, with the Studio's defaults filled in. */
interface PaneOptions {
  typography: boolean;
  colors: boolean;
  brandLogo: BrandLogoSwitch | null;
  preselect: (product: Product) => ProductImage | null | undefined;
  assets: boolean;
}

interface PanesProps {
  teamId: string;
  brandId: string;
  chosen: ProductChoice | null;
  subtitle: string;
  titleId: string;
  options: PaneOptions;
  onClose: () => void;
  onConfirm: (choice: ProductChoice, assets: PickedAssets) => void;
}

function Panes(props: PanesProps) {
  const { teamId, brandId, chosen, subtitle, titleId, options, onClose, onConfirm } = props;
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string | null>(chosen?.productId ?? null);
  const [selected, setSelected] = useState<string | null>(chosen?.assetId ?? null);
  // Each opening starts from the switch as the consumer holds it.
  const [brandLogo, setBrandLogo] = useState(options.brandLogo?.on === true);
  const products = useListProducts(teamId, brandId);
  const showProduct = (product: Product) => {
    setActiveId(product.id);
    setSelected(
      product.id === chosen?.productId
        ? chosen.assetId
        : (options.preselect({ ...product, images: photosOf(product) })?.asset_id ?? null),
    );
  };
  const active = products.data?.find((product) => product.id === activeId);
  const photo =
    active === undefined
      ? undefined
      : photosOf(active).find((image) => image.asset_id === selected);
  const confirm = () => {
    if (active === undefined || photo === undefined) return;
    onConfirm(
      { productId: active.id, assetId: photo.asset_id, url: photo.url, name: active.name },
      { brandLogo: brandLogo && options.brandLogo?.available === true },
    );
  };
  return (
    <div className={classes.panes} data-wide={active !== undefined || undefined}>
      <div className={classes.products}>
        <Text id={titleId} className={classes.title}>
          Products
        </Text>
        <Text className={classes.subtitle}>{subtitle}</Text>
        <TextInput
          className={classes.search}
          aria-label="Search by product name"
          placeholder="Search by product name"
          leftSection={<Search size={16} aria-hidden />}
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          rightSection={
            query === "" ? null : (
              <CloseButton size="sm" aria-label="Clear the search" onClick={() => setQuery("")} />
            )
          }
        />
        <ProductList products={products} query={query} activeId={activeId} onShow={showProduct} />
      </div>
      {active === undefined ? null : options.assets ? (
        <ImagesPane
          product={active}
          selected={selected}
          options={options}
          brandLogo={brandLogo}
          onBrandLogo={setBrandLogo}
          onSelect={setSelected}
          onConfirm={confirm}
          onClose={onClose}
        />
      ) : (
        <PhotosPane
          product={active}
          selected={selected}
          onSelect={setSelected}
          onConfirm={confirm}
          onClose={onClose}
        />
      )}
    </div>
  );
}

interface ProductListProps {
  products: UseQueryResult<Product[]>;
  query: string;
  activeId: string | null;
  onShow: (product: Product) => void;
}

/** Step 1's rows: each product's first photo and its name, filtered by the search. */
function ProductList({ products, query, activeId, onShow }: ProductListProps) {
  if (products.isPending) return <Loader size="sm" aria-label="Loading products" m="md" />;
  if (products.isError) return <ErrorAlert error={products.error} />;
  if (products.data.length === 0) {
    return <Text className={classes.empty}>No products yet. Add one on the Products page.</Text>;
  }
  const shown = products.data.filter((product) => matches(product, query));
  if (shown.length === 0) {
    return <Text className={classes.empty}>No product matches "{query.trim()}".</Text>;
  }
  return (
    <div className={classes.list}>
      {shown.map((product) => (
        <ProductRow
          key={product.id}
          product={product}
          active={product.id === activeId}
          onClick={() => onShow(product)}
        />
      ))}
    </div>
  );
}

function ProductRow(props: { product: Product; active: boolean; onClick: () => void }) {
  const url = firstPhoto(props.product)?.url ?? null;
  const [failed, setFailed] = useState(false);
  return (
    <UnstyledButton className={classes.row} aria-pressed={props.active} onClick={props.onClick}>
      {url === null || failed ? (
        <span className={classes.rowPicture} aria-hidden />
      ) : (
        <img src={url} alt="" className={classes.rowPicture} onError={() => setFailed(true)} />
      )}
      <span className={classes.rowName}>{props.product.name}</span>
    </UnstyledButton>
  );
}

interface ImagesPaneProps {
  product: Product;
  selected: string | null;
  options: PaneOptions;
  /** The working Brand Logo switch's state, where the consumer has one. */
  brandLogo: boolean;
  onBrandLogo: (on: boolean) => void;
  onSelect: (assetId: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

/** Step 2, beside the list: live's "Select Images & Assets" for the product shown. */
function ImagesPane(props: ImagesPaneProps) {
  const { product, selected, options, onSelect, onConfirm, onClose } = props;
  const photos = photosOf(product);
  const count = photos.some((image) => image.asset_id === selected) ? 1 : 0;
  return (
    <div className={classes.pane}>
      <div className={classes.paneHeader}>
        <Text className={classes.title}>Select Images &amp; Assets</Text>
        <CloseButton size="sm" aria-label="Close" onClick={onClose} />
      </div>
      <div className={classes.card}>
        <Text className={classes.cardTitle}>Brand Assets</Text>
        <Text className={classes.subtitle}>These settings apply to all selected products</Text>
        <div className={classes.switches}>
          {options.typography ? <SoonSwitch label="Typography" /> : null}
          {options.brandLogo === null ? (
            <SoonSwitch label="Brand Logo" />
          ) : (
            <LogoSwitch logo={options.brandLogo} on={props.brandLogo} onChange={props.onBrandLogo} />
          )}
        </div>
      </div>
      <div>
        <Text className={classes.title}>Product Customization</Text>
        <Text className={classes.subtitle}>Configure settings unique to each product</Text>
      </div>
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <Text className={classes.cardTitle}>{product.name}</Text>
          {options.colors ? <SoonSwitch label="Colors" /> : null}
        </div>
        <div className={classes.cardHeader}>
          <Text className={classes.label}>Images</Text>
          <Text className={classes.subtitle}>{count} selected</Text>
        </div>
        {photos.length === 0 ? (
          <Text className={classes.empty}>
            This product has no photo yet. Add one on the product's page.
          </Text>
        ) : (
          <div className={classes.tiles} role="radiogroup" aria-label={`Images of ${product.name}`}>
            {photos.map((image, at) => (
              <PhotoTile
                key={image.asset_id}
                image={image}
                group={`photos-${product.id}`}
                label={`${TAG_LABEL[image.tag]}, image ${at + 1}`}
                checked={image.asset_id === selected}
                onSelect={() => onSelect(image.asset_id)}
              />
            ))}
          </div>
        )}
      </div>
      <div className={classes.paneFooter}>
        <Button onClick={onConfirm} disabled={count === 0}>
          Confirm
        </Button>
      </div>
    </div>
  );
}

interface PhotosPaneProps {
  product: Product;
  selected: string | null;
  onSelect: (assetId: string) => void;
  onConfirm: () => void;
  onClose: () => void;
}

/** Step 2 of live's image slot's "From product" (`ProductImagesPanel`, issue 739): the product's
 * name, its photos to choose one from, and "Confirm". */
function PhotosPane(props: PhotosPaneProps) {
  const { product, selected, onSelect, onConfirm, onClose } = props;
  const photos = photosOf(product);
  const picked = photos.some((image) => image.asset_id === selected);
  return (
    <div className={classes.pane}>
      <div className={classes.paneHeader}>
        <Text className={classes.title}>{product.name}</Text>
        <CloseButton size="sm" aria-label="Close" onClick={onClose} />
      </div>
      {photos.length === 0 ? (
        <Text className={classes.empty}>This product has no usable images</Text>
      ) : (
        <div className={classes.tiles} role="radiogroup" aria-label={`Images of ${product.name}`}>
          {photos.map((image, at) => (
            <PhotoTile
              key={image.asset_id}
              image={image}
              group={`photos-${product.id}`}
              label={`${TAG_LABEL[image.tag]}, image ${at + 1}`}
              caption={false}
              checked={image.asset_id === selected}
              onSelect={() => onSelect(image.asset_id)}
            />
          ))}
        </div>
      )}
      <div className={classes.paneFooter}>
        <Button onClick={onConfirm} disabled={!picked}>
          Confirm
        </Button>
      </div>
    </div>
  );
}

interface PhotoTileProps {
  image: ProductImage;
  /** The radio group's name: one photo of the product. */
  group: string;
  label: string;
  /** Live's tag under the picture; its image slot's popover shows none. */
  caption?: boolean;
  checked: boolean;
  onSelect: () => void;
}

/** A photo to choose, as live's tile: the picture, its tag, and a check box on its corner. */
function PhotoTile({ image, group, label, caption = true, checked, onSelect }: PhotoTileProps) {
  const [failed, setFailed] = useState(false);
  return (
    <label className={classes.tile} data-checked={checked || undefined}>
      <input
        type="radio"
        name={group}
        className={classes.tileInput}
        aria-label={label}
        checked={checked}
        onChange={onSelect}
      />
      {image.url === null || failed ? (
        <span className={classes.tilePicture} aria-hidden />
      ) : (
        <img src={image.url} alt="" className={classes.tilePicture} onError={() => setFailed(true)} />
      )}
      {caption ? (
        <span className={classes.tileCaption} aria-hidden>
          {TAG_LABEL[image.tag]}
        </span>
      ) : null}
      <span className={classes.tileCheck} aria-hidden>
        {checked ? <Check size={12} strokeWidth={3} /> : null}
      </span>
    </label>
  );
}

/** Live's Brand Logo switch (its `LogoSettings`): off and disabled for a brand without a logo; on,
 * the logo under it. */
function LogoSwitch(props: { logo: BrandLogoSwitch; on: boolean; onChange: (on: boolean) => void }) {
  const { logo, on, onChange } = props;
  const shown = on && logo.available;
  return (
    <div className={classes.logoSetting}>
      <Switch
        label="Brand Logo"
        size="sm"
        checked={shown}
        disabled={!logo.available}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      {shown && logo.url !== null ? (
        <span className={classes.logoPreview}>
          <img src={logo.url} alt="Brand logo" className={classes.logoPicture} />
        </span>
      ) : null}
    </div>
  );
}

/** A switch live has here and the API does not take yet: off, and staying off. */
function SoonSwitch({ label }: { label: string }) {
  return (
    <Soon>
      {(soon) => (
        <Switch
          {...soon}
          label={label}
          size="sm"
          checked={false}
          onChange={() => undefined}
          classNames={{ root: classes.soonSwitch }}
        />
      )}
    </Soon>
  );
}
